import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { SCORE } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDateTime } from "@/lib/format";
import {
  Building2, MapPin, Layers, Boxes, FileText, ClipboardList, Activity,
  FolderCheck, ExternalLink, AlertTriangle, Search, MapPinned,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = {
  entity: Building2, site: MapPin, zone: Layers, asset: Boxes, contract: FileText,
  work_order: ClipboardList, indicator: Activity, evidence_pack: FolderCheck,
};

function RecIcon({ t, className }) {
  const I = TYPE_ICON[t] || FileText;
  return <I className={className} />;
}

export function ProvenanceList({ prov, onOpenRecord }) {
  if (!prov) return <p className="text-xs text-muted-foreground">Loading provenance…</p>;
  const s = prov.summary || {};
  const links = prov.links || [];
  return (
    <div className="space-y-3" data-testid={SCORE.provBlock}>
      <div className="rounded-lg border border-sage-light bg-sage-surface p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Where this number comes from</p>
        {s.captured ? (
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <div><span className="text-muted-foreground">How captured</span><br /><b>{s.method}</b>{s.value_desc ? ` · ${s.value_desc}` : ""}</div>
            <div><span className="text-muted-foreground">Evidence</span><br /><b>{s.evidence_kind}</b>{s.evidence_ref ? ` · ${s.evidence_ref}` : ""} <span className="text-muted-foreground">(Q{s.evidence_quality ?? 0})</span></div>
            <div><span className="text-muted-foreground">Created</span><br /><b>{s.created_by_name || "—"}</b><br />{s.created_at ? fmtDateTime(s.created_at) : "—"}</div>
            <div><span className="text-muted-foreground">Last updated</span><br /><b>{s.updated_by_name || "—"}</b><br />{s.updated_at ? fmtDateTime(s.updated_at) : "—"}</div>
            <div className="col-span-2"><span className="text-muted-foreground">Reviewer approval</span><br />
              {s.reviewer_approved
                ? <b className="text-emerald-700">Approved by {s.approved_by_name || "reviewer"}{s.approved_at ? ` · ${fmtDateTime(s.approved_at)}` : ""}</b>
                : <b className="text-amber-700">Awaiting reviewer approval</b>}
            </div>
          </div>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">No value has been captured for this KPI yet.</p>
        )}
      </div>

      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Linked source records ({links.length})</p>
        {links.length === 0 ? (
          prov.needs_backing ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900" data-testid={SCORE.provEmpty}>
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This number is scored but <b>not yet backed by any source record</b>. {prov.can_manage ? "Open the value dialog to attach the records it rests on." : "Ask the preparer to attach its source records."}</span>
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-sage-light p-3 text-xs text-muted-foreground" data-testid={SCORE.provEmptyNone}>No source records linked yet.</p>
          )
        ) : (
          <div className="divide-y rounded-lg border border-sage-light">
            {links.map((l, i) => (
              <div key={i} className="flex items-start justify-between gap-3 p-2.5">
                <div className="flex min-w-0 items-start gap-2">
                  <RecIcon t={l.record_type} className={`mt-0.5 h-4 w-4 shrink-0 ${l.missing ? "text-rose-400" : "text-forest-mid"}`} />
                  <div className="min-w-0">
                    <p className={`truncate text-sm font-medium ${l.missing ? "text-rose-600 line-through" : "text-forest-dark"}`}>
                      {l.label}{l.secondary ? <span className="ml-1 text-xs font-normal text-muted-foreground">· {l.secondary}</span> : null}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{l.group}{l.how_captured ? ` · ${l.how_captured}` : ""}</p>
                    {l.site_zone && (
                      <p className="mt-0.5 inline-flex items-center gap-1 rounded bg-sage-surface px-1.5 py-0.5 text-[10px] font-medium text-forest-dark">
                        <MapPinned className="h-3 w-3 text-forest-mid" /> {l.site_zone}
                      </p>
                    )}
                    {!l.missing && (
                      <p className="text-[11px] text-muted-foreground">
                        Created {l.created_by_name || "—"}{l.created_at ? ` · ${fmtDateTime(l.created_at)}` : ""} · Modified {l.modified_by_name || "—"}{l.modified_at ? ` · ${fmtDateTime(l.modified_at)}` : ""}
                      </p>
                    )}
                  </div>
                </div>
                {!l.missing && (
                  <Button size="sm" variant="ghost" className="h-7 shrink-0 text-xs text-forest-mid" onClick={() => onOpenRecord(l)} data-testid={SCORE.provLink(i)}>
                    Open <ExternalLink className="ml-1 h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function SourceRecordPicker({ open, onOpenChange, contractId, selected, onChange }) {
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get(`/score/source-records${contractId ? `?contract_id=${contractId}` : ""}`)
      .then((r) => setGroups(r.data.groups || []))
      .catch((e) => toast.error(apiErr(e)));
  }, [open, contractId]);

  const isSel = (t, id) => selected.some((s) => s.record_type === t && s.record_id === id);
  const toggle = (rec, grp) => {
    if (isSel(rec.record_type, rec.record_id)) {
      onChange(selected.filter((s) => !(s.record_type === rec.record_type && s.record_id === rec.record_id)));
    } else {
      onChange([...selected, {
        record_type: rec.record_type, record_id: rec.record_id, label: rec.label,
        secondary: rec.secondary, group: grp.group, is_master: grp.is_master,
      }]);
    }
  };

  const ql = q.trim().toLowerCase();
  const filtered = groups
    .map((g) => ({ ...g, records: g.records.filter((r) => !ql || (r.label || "").toLowerCase().includes(ql) || (r.secondary || "").toLowerCase().includes(ql)) }))
    .filter((g) => g.records.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden" data-testid={SCORE.sourcePicker}>
        <DialogHeader><DialogTitle className="font-heading">Attach source records</DialogTitle></DialogHeader>
        <p className="-mt-1 text-xs text-muted-foreground">Pick the records this number actually rests on. Everyone can see them; only preparers edit them.</p>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search records…" className="pl-8" data-testid={SCORE.sourceSearch} />
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">No matching records.</p>
          ) : filtered.map((g) => (
            <div key={g.record_type}>
              <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-forest-dark">
                <RecIcon t={g.record_type} className="h-3.5 w-3.5 text-forest-mid" /> {g.group} <span className="font-normal text-muted-foreground">· {g.is_master ? "data management" : "operational"}</span>
              </p>
              <div className="space-y-1">
                {g.records.map((rec) => (
                  <label key={rec.record_id} className="flex cursor-pointer items-center gap-2 rounded-md border border-sage-light p-2 text-sm hover:bg-sage-surface" data-testid={SCORE.sourceOption(rec.record_type, rec.record_id)}>
                    <Checkbox checked={isSel(rec.record_type, rec.record_id)} onCheckedChange={() => toggle(rec, g)} />
                    <span className="min-w-0 flex-1 truncate text-forest-dark">{rec.label}{rec.secondary ? <span className="ml-1 text-xs text-muted-foreground">· {rec.secondary}</span> : null}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="bg-forest-mid hover:bg-forest-dark" data-testid={SCORE.sourcePickerDone}>Done ({selected.length})</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
